import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import add_black from "../../../assets/images/icons/plus_black.svg";
import { GeneralButton, InfiniteScroll, Modal } from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import { usePostData } from "../../../hooks";
import { PatientDetails } from "../../../interface/GeneralResponse";
import { RootState } from "../../../redux/store";
import { useToast } from "../../../Services/ToastContext";
import { IsProfileCompleted } from "../../../Share";
import { AddNewPatient } from "../component";
import TemplateCard from "./TemplateCard";

function AddMedicalRecordToPatient() {
  const navigate = useNavigate();
  const [templatesData, setTemplatesData] = useState<any[]>();
  const [defaultTemplatesData, setDefaultTemplatesData] = useState<any[]>();
  const [openPatientModal, setOpenPatientModal] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [selectedPatient, setSelectedPatient] = useState<PatientDetails>();

  const { t } = useTranslation();

  const { mutate, isPending } = usePostData(EndPoints.LinkPatient);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const medicalRecord = useSelector(
    (state: RootState) => state.medicalRecord.record,
  );

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
  };

  const closeModel = () => {
    setOpenPatientModal(false);
    setStep(1);
    setSelectedTemplateId(null);
    setSelectedPatient(undefined);
  };

  const handleGoToStep2 = () => {
    const form = document.getElementById(
      "patient-details-form",
    ) as HTMLFormElement | null;

    form?.requestSubmit();
  }; 

  return (
    <div>
      <IsProfileCompleted
        title="complete_your_profile_to_start_adding_medical_record_to_patient"
        onAllowedClick={() => setOpenPatientModal(true)}
      >
        <GeneralButton
          title={t("add_medical_record_to_patient")}
          icon={add_black}
          color="white"
        />
      </IsProfileCompleted>

      <Modal
        isOpen={openPatientModal}
        onClose={closeModel}
        header={
          step === 1
            ? t("add_medical_record_to_patient")
            : t("choose_from_templates")
        }
        width={step === 2 ? "!max-w-[80vw]" : undefined}
        customStyle={step === 2 ? "min-w-[70vw] min-h-[78vh] relative" : ""}
      >
        {step === 1 && (
          <>
            <AddNewPatient
              onSubmit={(patient) => {
                setSelectedPatient(patient);
                setStep(2);
              }}
            />

            <div className="mt-28 flex justify-end gap-4">
              <GeneralButton
                title={t("cancel")}
                color="lite"
                fontColor="black"
                onClick={closeModel}
              />

              <GeneralButton
                title={
                  selectedPatient?.MedicalTemplateId
                    ? t("update_medical_report")
                    : t("add_medical_record_to_patient")
                }
                onClick={handleGoToStep2}
              />
            </div>
          </>
        )}

        {step === 2 && (
          <div className="h-full max-h-[calc(78vh-150px)] overflow-auto pt-2">
            <h2 className="my-2">{t("dome_care_templates")}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {defaultTemplatesData?.map((template: any, i) => (
                <TemplateCard
                  key={i}
                  item={template}
                  isSelectStyle
                  isSelected={selectedTemplateId === template.Id}
                  onSelect={handleTemplateSelect}
                />
              ))}

              <InfiniteScroll
                Count={10}
                skeletonCount={4}
                endpoint={EndPoints.MedicalHistoryTypes}
                getAllParams={{ IsGeneralMedical: true }}
                getData={(data) => setDefaultTemplatesData(data)}
                renderSkeleton={
                  <div className="h-40 animate-pulse rounded-lg bg-gray-400"></div>
                }
              />
            </div>

            <h3 className="my-2">{t("my_templates")}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {templatesData?.map((template: any, i) => (
                <TemplateCard
                  key={i}
                  item={template}
                  isSelectStyle
                  isSelected={selectedTemplateId === template.Id}
                  onSelect={handleTemplateSelect}
                />
              ))}

              <InfiniteScroll
                Count={10}
                skeletonCount={4}
                endpoint={EndPoints.MedicalHistoryTypes}
                getAllParams={{ IsGeneralMedical: false }}
                getData={(data) => setTemplatesData(data)}
                renderSkeleton={
                  <div className="h-40 animate-pulse rounded-lg bg-gray-400"></div>
                }
              />
            </div>

            <div className="absolute bottom-1 end-4 flex w-full justify-end gap-2 py-5">
              <GeneralButton
                title={t("cancel")}
                color="lite"
                fontColor="black"
                onClick={closeModel}
              />
              <GeneralButton
                title={t("start_creating_record")}
                isLoading={isPending}
                disabled={selectedTemplateId ? false : true}
                onClick={() => {
                  if (!selectedPatient?.Id || !selectedTemplateId) return;

                  if (!selectedPatient.IsLinked) { 
                    
                    mutate(
                      {
                        Id: selectedPatient.Id,
                        Birthday: selectedPatient.Birthday,
                        Gender: selectedPatient.Gender,
                      },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({
                            queryKey: [EndPoints.GetMyPatient],
                          });

                          showToast(
                            t("patient_added_to_your_patient_list"),
                            "success",
                          );

                          navigate(
                            `/patient/${selectedPatient.Id}/add-medical-record/${selectedTemplateId}`,
                            {
                              state: {
                                medicalRecord,
                              },
                            },
                          );
                        },
                        onError: (error) => {
                          showToast(error.message, "error");
                        },
                      },
                    );
                  } else {
                    navigate(
                      `/patient/${selectedPatient.Id}/add-medical-record/${selectedTemplateId}`,
                      {
                        state: {
                          medicalRecord,
                        },
                      },
                    );
                  }
                }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AddMedicalRecordToPatient;
